// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Group, LoadingOverlay, Skeleton, Space, Stack, Text } from '@mantine/core';
import { useContext, useRef, useState } from 'react';
import { useElementSize } from '@mantine/hooks';
import { useDrop } from 'react-dnd-latest';
import BookitupSegmentedControl from '../../../Atoms/BookitupSegmentedControl';
import { DocumentTemplateContext } from '../../../Service/Context/DocumentTemplateContext';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { DataS } from '../../../Service/DataS';
import { DragItem, PositionDragItem, TaxType } from '../../../Service/DocumentT';
import PositionHeader from '../../Document/Position/PositionHeader';
import { RichInput } from '../../../Organismns/tiptap/TiptapEditorInput';
import PositionGroups from '../../Document/Position/PositionGroups';
import DocumentPosition from '../../Document/Position/DocumentPositionV2';
import DropArea from '../../../Atoms/DropArea';
import DocumentSum from '../../Document/DocumentSum';
import SkeletonItems, { EmptyPositionsSkeleton } from '../../../Atoms/SkeletonItems';
import { openAddProductModal } from '../../Document/Position/AddProductModal';
import DocumentBubbleMenu from '../../../Organismns/tiptap/toolbars/BubbleMenu';
import { usePositionProducts } from '../../../Service/hooks/usePositionProducts';
import { ThemeText } from '../../../Atoms/ThemeText';
import { LicenceS } from '../../../Service/LicenceS';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import BookitupNumberInput from '../../../Atoms/inputs/BookitupNumberInput';
import DocumentLanguageS from '../../../Service/DocumentLanguageS';
import LinkText from '../../../Atoms/LinkText';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { useJwtClaims } from '../../../Service/Context/AuthContext';

const DocumentTemplateSkeleton = () => {
  const { ref, width } = useElementSize();
  const { licence } = useJwtClaims();
  const positionsSectionRef = useRef<HTMLDivElement>(null);
  const { profile } = useContext(ProfileContext);
  const { sm: isMobile } = useContext(WindowContext);
  const [addingPosition, setAddingPosition] = useState(false);
  const { products } = usePositionProducts();
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { loading, bookitupDocument, savePatch, positionHandlers, groupHandlers, refresh } =
    useContext(DocumentTemplateContext);
  const { addGroup, addingGroupAtIndex, movingGroup } = groupHandlers;
  const { addPosition, removePositionFromGroup, addProducts } = positionHandlers;
  const hasFlowLicence = LicenceS.hasSuitableLicence('FLOW', licence);

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DragItem.POSITION,
      canDrop: (item: PositionDragItem) => item.position.groupId !== null,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: !!monitor.canDrop(),
      }),
      drop: (item: PositionDragItem) => removePositionFromGroup(item.position.id),
    }),
    [removePositionFromGroup],
  );

  const addProduct = () =>
    executeWithLicenceCheck(LicenceS.Restrictions.PRODUCT_USAGE, () => {
      openAddProductModal({ products, onFinish: (p) => addProducts(p) });
    });

  const addGroupHandler = () => {
    addGroup();
    positionsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const addPositionHandler = async () => {
    setAddingPosition(true);
    try {
      await addPosition();
    } finally {
      setAddingPosition(false);
    }
  };

  const { documentType, taxType, positions, positionGroups, currencySymbol, language } = bookitupDocument ?? {
    positions: [],
    positionGroups: [],
  };
  const { smallScaleBusiness } = profile ?? {};
  const emptyGroups = positionGroups.length === 0;
  const grouplessPositions = positions.filter((position) => position.groupId === null);
  const emptyPositions = grouplessPositions.length === 0;
  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';
  const isReminder = documentType === 'REMINDER';
  const usePreviewVersion = width > 0 && width < 800;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <LoadingOverlay visible={movingGroup} overlayBlur={2} loaderProps={{ size: 'xl' }} />
      <Group position="apart" mb="md">
        <ThemeText size="lg" weight="bolder">
          {loading && 'Laden ...'}
          {!loading && documentType && DocumentLanguageS.documentType(language, documentType)}
          {!loading && !documentType && 'Vorlage'}
        </ThemeText>
        {!smallScaleBusiness && (
          <BookitupSegmentedControl
            value={taxType}
            color="blue"
            data={DataS.netGrossOptions}
            radius="md"
            onChange={(val: TaxType) => savePatch({ taxType: val })}
            sx={{ minWidth: 160 }}
          />
        )}
      </Group>
      {bookitupDocument && (
        <RichInput
          label="Kopftext"
          value={bookitupDocument.headRow}
          patchField={(val: string) => savePatch({ headRow: val })}
          menu={<DocumentBubbleMenu />}
        />
      )}
      <Space h="xs" />
      <PositionHeader
        documentVersion
        currencySymbol={currencySymbol}
        desktopVersion={!usePreviewVersion}
        showDiscount={!isPartialInvoice}
        isPartialInvouce={documentType === 'PARTIAL_INVOICE'}
        language={bookitupDocument?.language}
        sticky={!isMobile}
      />
      <Stack ref={positionsSectionRef} mt={5} sx={{ scrollMarginTop: 80 }}>
        <PositionGroups usePreviewVersion={usePreviewVersion} products={products} />
        {addingGroupAtIndex !== null && emptyGroups && <Skeleton ml={30} height={56} radius="md" />}
        {loading && <SkeletonItems height={100} />}
        {!loading && !addingPosition && emptyPositions && emptyGroups && addingGroupAtIndex === null && (
          <EmptyPositionsSkeleton />
        )}
        {bookitupDocument &&
          grouplessPositions.map((position) => (
            <DocumentPosition
              key={position.id}
              bookitupDocument={bookitupDocument}
              position={position}
              handlers={positionHandlers}
              refreshDocument={refresh}
              previewVersion={usePreviewVersion}
              products={products}
            />
          ))}
      </Stack>
      {/* Keep stable geometry so native HTML5 drag hit-testing can discover this target before a drag starts. */}
      <div ref={drop} style={{ marginBottom: 10, minHeight: 74 }} aria-label="Positionen ohne Gruppe">
        {canDrop && <DropArea isOver={isOver} />}
      </div>
      {addingPosition && <Skeleton mt="sm" width="100%" height={100} />}
      {!isReminder && !canDrop && !addingPosition && !loading && (
        <Group mt="md" mb="md">
          {!isPartialInvoice && emptyPositions && (
            <>
              <LinkText onClick={addPositionHandler}>+ Position hinzufügen</LinkText>
              <LinkText onClick={addProduct}>+ Produkt hinzufügen</LinkText>
            </>
          )}
          {emptyGroups && hasFlowLicence && (
            <LinkText onClick={() => addingGroupAtIndex === null && addGroupHandler()}>+ Gruppe hinzufügen</LinkText>
          )}
        </Group>
      )}
      {bookitupDocument && <DocumentSum baseDocument={bookitupDocument} savePatch={savePatch} />}
      <Space h="xl" />
      {bookitupDocument && (
        <RichInput
          label="Fußtext"
          value={bookitupDocument.endRow}
          patchField={(val: string) => savePatch({ endRow: val })}
          menu={<DocumentBubbleMenu />}
          styles={{ minHeight: 150 }}
        />
      )}
    </div>
  );
};

export default DocumentTemplateSkeleton;
